import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowRight,
  Building2,
  Globe,
  Instagram,
  KeyRound,
  Lock,
  Mail,
  MapPin,
  Phone,
  RotateCcw,
  ShieldCheck,
  Store,
  User,
} from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { BRAZIL_STATES, SUPPLIER_CATEGORIES } from '../lib/suppliers';
import { SUPPLIERS_BETA_ENABLED } from '../lib/featureFlags';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;

export default function SupplierRegister() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [step, setStep] = useState('form');
  const [challengeId, setChallengeId] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    name: '',
    email: '',
    phone: '',
    document: '',
    password: '',
    confirmPassword: '',
    category: '',
    city: '',
    state: '',
    short_description: '',
    instagram: '',
    whatsapp: '',
    website_url: '',
  });

  if (!SUPPLIERS_BETA_ENABLED) {
    return <Navigate to="/" replace />;
  }

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('As senhas nao coincidem.');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    const documentDigits = String(formData.document || '').replace(/\D/g, '');
    if (![11, 14].includes(documentDigits.length)) {
      toast.error('Informe um CPF ou CNPJ valido.');
      return;
    }

    if (!formData.company_name.trim()) {
      toast.error('Informe o nome da empresa ou marca.');
      return;
    }

    if (!formData.category) {
      toast.error('Escolha a categoria principal.');
      return;
    }

    if (!formData.city.trim() || !formData.state.trim()) {
      toast.error('Informe cidade e estado.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/suppliers/register`, {
        company_name: formData.company_name.trim(),
        contact_name: formData.name.trim(),
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        document: documentDigits,
        document_type: documentDigits.length === 14 ? 'cnpj' : 'cpf',
        password: formData.password,
        category: formData.category,
        city: formData.city.trim(),
        state: formData.state.trim().toUpperCase(),
        short_description: formData.short_description.trim(),
        instagram: formData.instagram.trim(),
        whatsapp: formData.whatsapp.trim() || formData.phone.trim(),
        website_url: formData.website_url.trim(),
      });

      if (response.data?.requires_2fa) {
        setStep('verify_2fa');
        setChallengeId(response.data.challenge_id || '');
        setMaskedEmail(response.data.masked_email || formData.email.trim().toLowerCase());
        setTwoFactorCode('');
        toast.success('Codigo enviado para confirmar seu perfil.');
        return;
      }

      toast.success('Cadastro enviado com sucesso.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar cadastro de fornecedor.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if ((twoFactorCode || '').replace(/\D/g, '').length !== 6) {
      toast.error('Digite o codigo de 6 digitos enviado por e-mail.');
      return;
    }

    setVerifying(true);
    try {
      await axios.post(
        `${API}/suppliers/2fa/verify`,
        { challenge_id: challengeId, code: twoFactorCode },
        { withCredentials: true }
      );
      toast.success('Perfil confirmado. Agora sua conta entra em analise.');
      navigate('/fornecedor/painel');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nao foi possivel validar o codigo.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!challengeId) return;
    setResending(true);
    try {
      const response = await axios.post(`${API}/suppliers/2fa/resend`, { challenge_id: challengeId });
      setChallengeId(response.data?.challenge_id || challengeId);
      setMaskedEmail(response.data?.masked_email || maskedEmail);
      setTwoFactorCode('');
      toast.success('Novo codigo enviado para o e-mail cadastrado.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nao foi possivel reenviar o codigo.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F1EF] flex">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#E8B7B1]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.18),transparent_40%)]" />
        <div className="relative z-10 p-14 flex flex-col justify-center max-w-xl">
          <BrandLogo size="md" withText={false} iconClassName="w-14 h-14 mb-8" />
          <p className="text-sm uppercase tracking-[0.35em] text-white/70 mb-4">Area de fornecedores</p>
          <h1 className="text-5xl leading-tight font-semibold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Crie seu perfil profissional e apareca para casais dentro do Virei Noiva.
          </h1>
          <p className="text-lg text-white/82 mb-10">
            Este cadastro abre sua entrada no diretorio. Depois da confirmacao por e-mail, seu perfil fica em analise e pode aparecer na vitrine de fornecedores quando aprovado.
          </p>

          <div className="space-y-4">
            {[
              ['1', 'Cadastre seu negocio', 'Informe sua marca, categoria, cidade e dados principais de contato.'],
              ['2', 'Confirme seu e-mail', 'Validamos o acesso com um codigo antes de liberar o painel do fornecedor.'],
              ['3', 'Entre em analise', 'A equipe revisa o perfil antes da publicacao publica.'],
              ['4', 'Apareca na vitrine', 'Fornecedores aprovados entram no diretorio e podem aparecer em destaques na home.'],
            ].map(([stepNumber, title, description]) => (
              <div key={stepNumber} className="flex items-start gap-4 rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-base font-bold text-white">
                  {stepNumber}
                </div>
                <div>
                  <p className="font-semibold text-white">{title}</p>
                  <p className="text-sm text-white/78">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-2xl rounded-[2rem] border border-[#E8B7B1]/35 bg-white/92 p-8 shadow-[0_30px_80px_rgba(96,72,58,0.12)] backdrop-blur">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-[#1C1C1C] mb-6">
              <BrandLogo size="sm" />
            </Link>
            <h2 className="text-3xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
              {step === 'form' ? 'Cadastro de fornecedor' : 'Confirmar cadastro'}
            </h2>
              <p className="mt-3 text-[#5B4A45]">
                {step === 'form'
                  ? 'Crie seu perfil no diretorio do Virei Noiva.'
                  : `Enviamos um codigo para ${maskedEmail || 'o e-mail informado'}.`}
              </p>
          </div>

          {step === 'form' ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Empresa ou marca</Label>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                    <Input
                      value={formData.company_name}
                      onChange={(event) => updateField('company_name', event.target.value)}
                      placeholder="Nome da sua marca"
                      className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Responsavel</Label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                    <Input
                      value={formData.name}
                      onChange={(event) => updateField('name', event.target.value)}
                      placeholder="Seu nome"
                      className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">E-mail</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(event) => updateField('email', event.target.value)}
                      placeholder="contato@empresa.com"
                      autoComplete="email"
                      className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12"
                      required
                      data-testid="supplier-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Telefone</Label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                    <Input
                      value={formData.phone}
                      onChange={(event) => updateField('phone', event.target.value)}
                      placeholder="(11) 99999-9999"
                      autoComplete="tel"
                      className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Categoria</Label>
                  <div className="relative">
                    <Store className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                    <select
                      value={formData.category}
                      onChange={(event) => updateField('category', event.target.value)}
                      className="h-12 w-full rounded-xl border border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12 pr-4 text-[#1C1C1C]"
                      required
                    >
                      <option value="">Escolha a categoria</option>
                      {SUPPLIER_CATEGORIES.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Cidade</Label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                    <Input
                      value={formData.city}
                      onChange={(event) => updateField('city', event.target.value)}
                      placeholder="Sua cidade"
                      className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Estado</Label>
                  <select
                    value={formData.state}
                    onChange={(event) => updateField('state', event.target.value)}
                    className="h-12 w-full rounded-xl border border-[#E8B7B1]/35 bg-[#F9F5F3] px-4 text-[#1C1C1C]"
                    required
                  >
                    <option value="">UF</option>
                    {BRAZIL_STATES.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">CPF ou CNPJ</Label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                  <Input
                    value={formData.document}
                    onChange={(event) => updateField('document', event.target.value)}
                    placeholder="Digite seu CPF ou CNPJ"
                    autoComplete="off"
                    className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Descricao curta</Label>
                <Textarea
                  value={formData.short_description}
                  onChange={(event) => updateField('short_description', event.target.value)}
                  placeholder="Apresente em poucas linhas o que voce oferece para os casais."
                  className="min-h-[110px] rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3]"
                  required
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">WhatsApp</Label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                    <Input
                      value={formData.whatsapp}
                      onChange={(event) => updateField('whatsapp', event.target.value)}
                      placeholder="(11) 99999-9999"
                      className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Instagram</Label>
                  <div className="relative">
                    <Instagram className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                    <Input
                      value={formData.instagram}
                      onChange={(event) => updateField('instagram', event.target.value)}
                      placeholder="@seuperfil"
                      className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Site</Label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                    <Input
                      value={formData.website_url}
                      onChange={(event) => updateField('website_url', event.target.value)}
                      placeholder="www.seusite.com"
                      className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12"
                    />
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(event) => updateField('password', event.target.value)}
                      placeholder="Minimo 6 caracteres"
                      autoComplete="new-password"
                      className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12"
                      minLength={6}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Confirmar senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                    <Input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(event) => updateField('confirmPassword', event.target.value)}
                      placeholder="Digite a senha novamente"
                      autoComplete="new-password"
                      className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-[#E8B7B1]/35 bg-[#F6F1EF] p-4 text-sm text-[#5B4A45]">
                Seu perfil nao entra automaticamente na vitrine publica. Primeiro validamos o e-mail e depois a equipe aprova o cadastro antes da publicacao.
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="mt-2 h-12 w-full rounded-full bg-[#E8B7B1] text-white hover:bg-[#DFA8A1]"
              >
                {loading ? 'Enviando codigo...' : (
                  <span className="inline-flex items-center gap-2">
                    Criar meu perfil <ArrowRight className="h-5 w-5" />
                  </span>
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-5">
              <div className="rounded-2xl border border-[#E8B7B1]/35 bg-[#F6F1EF] p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 text-[#C8A97E]" />
                  <div>
                    <p className="font-semibold text-[#1C1C1C]">Seguranca do fornecedor</p>
                    <p className="text-sm text-[#5B4A45]">
                      Confirme o codigo para liberar sua analise e acessar o painel do fornecedor.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Codigo de verificacao</Label>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7E6A64]" />
                  <Input
                    value={twoFactorCode}
                    onChange={(event) => setTwoFactorCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    autoComplete="one-time-code"
                    className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3] pl-12 text-center tracking-[0.35em]"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={verifying}
                className="h-12 w-full rounded-full bg-[#E8B7B1] text-white hover:bg-[#DFA8A1]"
              >
                {verifying ? 'Validando...' : 'Confirmar cadastro'}
              </Button>

              <div className="grid gap-2 sm:grid-cols-2">
                <Button type="button" variant="outline" onClick={handleResend} disabled={resending} className="rounded-full">
                  {resending ? 'Reenviando...' : 'Reenviar codigo'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="rounded-full"
                  onClick={() => {
                    setStep('form');
                    setChallengeId('');
                    setMaskedEmail('');
                    setTwoFactorCode('');
                  }}
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Voltar
                </Button>
              </div>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-[#5B4A45]">
            Ja tem acesso?{' '}
            <Link to="/fornecedor/login" className="font-semibold text-[#1C1C1C] hover:underline">
              Entrar na area de fornecedores
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
